import { useState } from "react";
import { ChefHat, Clock, Users, ChevronDown, Printer, FileDown } from "lucide-react";
import { printRecipe, saveRecipePdf } from "@/lib/share";

// Recipe card for food manuals. Collapsible (used minimized inside the step viewer).
export const RecipeCard = ({ recipe, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!recipe || !recipe.ingredients?.length) return null;

  return (
    <div className="relative rounded-2xl border border-[#5d8a52] bg-white overflow-hidden" data-testid="recipe-card">
      {!collapsible && (
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1 z-10" data-testid="recipe-actions">
          <button
            type="button"
            data-testid="recipe-print-btn"
            onClick={() => printRecipe(recipe)}
            title="Print recipe"
            className="p-2 rounded-lg text-[#4f7746] hover:bg-[#eef3ea] transition-colors"
          >
            <Printer size={17} />
          </button>
          <button
            type="button"
            data-testid="recipe-pdf-btn"
            onClick={() => saveRecipePdf(recipe)}
            title="Save recipe as PDF"
            className="p-2 rounded-lg text-[#4f7746] hover:bg-[#eef3ea] transition-colors"
          >
            <FileDown size={17} />
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => collapsible && setOpen((o) => !o)}
        className={`w-full flex items-center gap-3 px-4 py-3 text-left ${!collapsible ? "pr-24" : ""} ${collapsible ? "cursor-pointer hover:bg-[#faf8ff]" : "cursor-default"}`}
        data-testid="recipe-card-header"
      >
        <div className="w-9 h-9 rounded-xl bg-[#eef3ea] flex items-center justify-center shrink-0">
          <ChefHat size={18} className="text-[#4f7746]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-wide text-[#9a96ac]">Recipe</div>
          <div className="font-extrabold text-[#171426] truncate" data-testid="recipe-name">{recipe.name}</div>
        </div>
        {collapsible && (
          <ChevronDown size={20} className={`text-[#9a96ac] transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {recipe.servings && (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4f7746] bg-[#eef3ea] px-3 py-1 rounded-full">
                <Users size={13} /> {recipe.servings}
              </span>
            )}
            {recipe.prep_time && (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4f7746] bg-[#eef3ea] px-3 py-1 rounded-full">
                <Clock size={13} /> Prep {recipe.prep_time}
              </span>
            )}
            {recipe.cook_time && (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4f7746] bg-[#eef3ea] px-3 py-1 rounded-full">
                <Clock size={13} /> Cook {recipe.cook_time}
              </span>
            )}
          </div>
          <div className="text-sm font-bold text-[#4a475c] mb-2">Ingredients</div>
          <ul className="space-y-1.5">
            {recipe.ingredients.map((ing, i) => (
              <li key={i} className="flex gap-2 text-sm text-[#4a475c]" data-testid={`recipe-ingredient-${i}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#5d8a52] mt-2 shrink-0" />
                <span>
                  {ing.quantity && <span className="font-semibold text-[#171426]">{ing.quantity} </span>}
                  {ing.item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
